import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const PageHistoryModal = ({ pageId, siteId, isOpen, onClose }) => {
  const { currentLang, token, API_URL } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [showHtml, setShowHtml] = useState(false);
  const [viewMode, setViewMode] = useState('seo'); // 'seo', 'html', 'iframe'
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);

  // Загружаем историю при открытии модалки
  useEffect(() => {
    if (isOpen && pageId && siteId) {
      fetchHistory();
    }
  }, [isOpen, pageId, siteId]);

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Очищаем blob URL при размонтировании компонента
  useEffect(() => {
    return () => {
      if (iframeUrl) {
        URL.revokeObjectURL(iframeUrl);
      }
    };
  }, [iframeUrl]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/pages/${pageId}/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.history) {
        setHistory(data.history);
        // Устанавливаем самую последнюю запись как выбранную по умолчанию
        if (data.history.length > 0) {
          setSelectedHistoryItem(data.history[0]);
          setSelectedDate(data.history[0].date);
        }
      } else {
        throw new Error(data.message || 'Ошибка загрузки истории');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleDateSelect = (historyItem) => {
    // Очищаем предыдущий iframe URL
    if (iframeUrl) {
      URL.revokeObjectURL(iframeUrl);
    }
    
    setSelectedHistoryItem(historyItem);
    setSelectedDate(historyItem.date);
    setViewMode('seo'); // Сбрасываем режим просмотра при смене даты
    
    // Создаем новый iframe URL
    const newIframeUrl = createIframeUrl(historyItem.html_code);
    setIframeUrl(newIframeUrl);
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const handleIframeError = () => {
    setIframeLoading(false);
  };

  // Анализируем SEO элементы из HTML кода
  const analyzeSeoElements = (htmlCode) => {
    if (!htmlCode) return null;
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlCode, 'text/html');
      
      return {
        title: doc.querySelector('title')?.textContent?.trim() || null,
        metaDescription: doc.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() || null,
        metaKeywords: doc.querySelector('meta[name="keywords"]')?.getAttribute('content')?.trim() || null,
        headings: {
          h1: Array.from(doc.querySelectorAll('h1')).map(h => h.textContent.trim()).filter(Boolean),
          h2: Array.from(doc.querySelectorAll('h2')).map(h => h.textContent.trim()).filter(Boolean),
          h3: Array.from(doc.querySelectorAll('h3')).map(h => h.textContent.trim()).filter(Boolean),
          h4: Array.from(doc.querySelectorAll('h4')).map(h => h.textContent.trim()).filter(Boolean),
          h5: Array.from(doc.querySelectorAll('h5')).map(h => h.textContent.trim()).filter(Boolean),
        }
      };
    } catch (error) {
      return null;
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'iframe' && selectedHistoryItem) {
      setIframeLoading(true);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
    }).catch(err => {
    });
  };

  // Извлекаем базовый URL из HTML кода
  const extractBaseUrl = (htmlCode) => {
    try {
      // Ищем base href
      const baseMatch = htmlCode.match(/<base[^>]*href\s*=\s*["']([^"']*)["']/i);
      if (baseMatch) {
        return baseMatch[1];
      }
      
      // Ищем абсолютные URL в ссылках
      const linkMatch = htmlCode.match(/<link[^>]*href\s*=\s*["'](https?:\/\/[^"']*)["']/i);
      if (linkMatch) {
        const url = new URL(linkMatch[1]);
        return `${url.protocol}//${url.host}/`;
      }
      
      // Ищем абсолютные URL в изображениях
      const imgMatch = htmlCode.match(/<img[^>]*src\s*=\s*["'](https?:\/\/[^"']*)["']/i);
      if (imgMatch) {
        const url = new URL(imgMatch[1]);
        return `${url.protocol}//${url.host}/`;
      }
      
      return 'https://example.com/';
    } catch (error) {
      return 'https://example.com/';
    }
  };

  // Создаем blob URL для iframe
  const createIframeUrl = (htmlCode) => {
    if (!htmlCode) return null;
    
    try {
      const baseUrl = extractBaseUrl(htmlCode);
      // Очищаем HTML от потенциально опасных элементов
      let cleanHtml = htmlCode;
      
      // Удаляем только потенциально опасные элементы
      cleanHtml = cleanHtml.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
      cleanHtml = cleanHtml.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
      cleanHtml = cleanHtml.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '');
      cleanHtml = cleanHtml.replace(/<embed\b[^>]*>/gi, '');
      
      // Удаляем javascript: протоколы из href и src атрибутов
      cleanHtml = cleanHtml.replace(/javascript:/gi, '');
      
      // Удаляем все on* атрибуты (onclick, onload, onmouseover и т.д.)
      cleanHtml = cleanHtml.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
      cleanHtml = cleanHtml.replace(/\s+on\w+\s*=\s*[^"'\s>]+/gi, '');
      
      // Удаляем только интерактивные элементы, но сохраняем стили
      cleanHtml = cleanHtml.replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, '');
      cleanHtml = cleanHtml.replace(/<input\b[^>]*>/gi, '');
      cleanHtml = cleanHtml.replace(/<button\b[^>]*>/gi, '');
      cleanHtml = cleanHtml.replace(/<select\b[^<]*(?:(?!<\/select>)<[^<]*)*<\/select>/gi, '');
      cleanHtml = cleanHtml.replace(/<textarea\b[^<]*(?:(?!<\/textarea>)<[^<]*)*<\/textarea>/gi, '');
      
      // Обрабатываем ссылки - убираем только потенциально опасные атрибуты
      cleanHtml = cleanHtml.replace(/<a\b([^>]*)>/gi, (match, attributes) => {
        try {
          // Убираем только опасные атрибуты, сохраняем остальные
          let cleanAttributes = attributes
            .replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '') // Убираем on* атрибуты
            .replace(/\s+on\w+\s*=\s*[^"'\s>]+/gi, '') // Убираем on* атрибуты без кавычек
            .replace(/\s+target\s*=\s*["'][^"']*["']/gi, '') // Убираем target
            .replace(/\s+rel\s*=\s*["'][^"']*["']/gi, ''); // Убираем rel
          
          // Добавляем безопасные атрибуты
          cleanAttributes += ' target="_blank" rel="noopener noreferrer"';
          
          return `<a${cleanAttributes}>`;
        } catch (error) {
          return match; // Возвращаем оригинальную ссылку в случае ошибки
        }
      });
      
      // Обрабатываем внешние стили - добавляем base URL для корректной загрузки
      cleanHtml = cleanHtml.replace(
        /<link\s+rel\s*=\s*["']stylesheet["'][^>]*>/gi,
        (match) => {
          // Добавляем base URL если его нет
          if (!match.includes('http') && !match.includes('//')) {
            return match.replace('href="', `href="${baseUrl}`);
          }
          return match;
        }
      );
      
      // Обрабатываем изображения - добавляем base URL для относительных путей
      cleanHtml = cleanHtml.replace(
        /<img\s+([^>]*)>/gi,
        (match, attributes) => {
          // Если src относительный, добавляем base URL
          if (attributes.includes('src="') && !attributes.includes('src="http') && !attributes.includes('src="//')) {
            return match.replace('src="', `src="${baseUrl}`);
          }
          return match;
        }
      );
      
      // Создаем полный HTML документ
      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Историческая версия страницы</title>
          <base href="${baseUrl}" target="_blank">
          <style>
            /* Только базовые стили для безопасности, не переопределяем стили страницы */
            body { 
              margin: 0; 
              padding: 0; 
            }
            /* Отключаем только потенциально опасные элементы */
            script, iframe, object, embed, form, input, button, select, textarea {
              display: none !important;
            }
            /* Безопасные стили для ссылок в iframe */
            a {
              color: inherit !important;
              text-decoration: inherit !important;
            }
            a:hover {
              color: inherit !important;
              text-decoration: inherit !important;
            }
            /* Безопасные стили для изображений (только базовые ограничения) */
            img {
              max-width: 100% !important;
              height: auto !important;
            }
            /* Изоляция iframe от внешних стилей */
            html, body {
              isolation: isolate;
            }
          </style>
        </head>
        <body>
          ${cleanHtml}
        </body>
        </html>
      `;
      
      const blob = new Blob([fullHtml], { type: 'text/html' });
      return URL.createObjectURL(blob);
    } catch (error) {
      
      return null;
    }
  };

  if (!isOpen) return null;

  // Показываем состояние загрузки
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">
                {currentLang === 'ru' ? 'Загрузка истории изменений...' : 'Loading change history...'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                {currentLang === 'ru' ? 'История изменений' : 'Change History'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-red-50 p-4 border-l-4 border-red-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {currentLang === 'ru' ? 'Ошибка загрузки истории' : 'Error loading history'}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={fetchHistory}
                      className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      {currentLang === 'ru' ? 'Попробовать снова' : 'Try again'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="flex flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">
              {currentLang === 'ru' ? 'История изменений' : 'Page History'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {history.length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {currentLang === 'ru' ? 'История изменений пуста' : 'No change history'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {currentLang === 'ru' ? 'История изменений этой страницы пока не доступна' : 'Change history for this page is not available yet'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Календарь с датами */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">
                    {currentLang === 'ru' ? 'Даты изменений' : 'Change Dates'} ({history.length})
                  </h4>
                  
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {history.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => handleDateSelect(item)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedHistoryItem?.id === item.id
                            ? 'bg-blue-50 border-blue-300 text-blue-800'
                            : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium">
                              {formatDateShort(item.date)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(item.date).toLocaleTimeString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Контент выбранной даты */}
              <div className="lg:col-span-2">
                {selectedHistoryItem ? (
                  <div className="space-y-4">
                    {/* Заголовок выбранной даты */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-blue-800">
                            {formatDate(selectedHistoryItem.date)}
                          </h4>
                          <p className="text-sm text-blue-600 mt-1">
                            {currentLang === 'ru' ? 'Версия страницы на эту дату' : 'Page version for this date'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="flex rounded-md shadow-sm">
                            <button
                              onClick={() => handleViewModeChange('seo')}
                              className={`px-3 py-2 text-sm font-medium rounded-l-md transition-colors ${
                                viewMode === 'seo'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {currentLang === 'ru' ? 'SEO' : 'SEO'}
                            </button>
                            <button
                              onClick={() => handleViewModeChange('iframe')}
                              className={`px-3 py-2 text-sm font-medium transition-colors border-t border-b ${
                                viewMode === 'iframe'
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {currentLang === 'ru' ? 'Страница' : 'Page'}
                            </button>
                            <button
                              onClick={() => handleViewModeChange('html')}
                              className={`px-3 py-2 text-sm font-medium rounded-r-md transition-colors ${
                                viewMode === 'html'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {currentLang === 'ru' ? 'HTML' : 'HTML'}
                            </button>
                          </div>
                          {viewMode === 'html' && (
                            <button
                              onClick={() => copyToClipboard(selectedHistoryItem.html_code)}
                              className="px-3 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                              title={currentLang === 'ru' ? 'Копировать HTML код' : 'Copy HTML code'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* SEO анализ исторической версии */}
                    {viewMode === 'seo' && (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-700">
                            {currentLang === 'ru' ? 'SEO анализ' : 'SEO Analysis'}
                          </span>
                        </div>
                        <div className="p-4">
                          {selectedHistoryItem.html_code ? (
                            (() => {
                              const seoData = analyzeSeoElements(selectedHistoryItem.html_code);
                              return seoData ? (
                                <div className="space-y-6">
                                  {/* Title */}
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                      <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                      </svg>
                                      {currentLang === 'ru' ? 'Title' : 'Title'}
                                    </h4>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                      {seoData.title ? (
                                        <div className="text-sm text-gray-800 break-words">
                                          {seoData.title}
                                          <div className="mt-1 text-xs text-gray-500">
                                            {seoData.title.length} символов
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-sm text-red-500 italic">
                                          {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Meta Description */}
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                      <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      {currentLang === 'ru' ? 'Meta Description' : 'Meta Description'}
                                    </h4>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                      {seoData.metaDescription ? (
                                        <div className="text-sm text-gray-800 break-words">
                                          {seoData.metaDescription}
                                          <div className="mt-1 text-xs text-gray-500">
                                            {seoData.metaDescription.length} символов
                                            {seoData.metaDescription.length < 120 && (
                                              <span className="text-yellow-600 ml-2">
                                                {currentLang === 'ru' ? '(слишком короткий)' : '(too short)'}
                                              </span>
                                            )}
                                            {seoData.metaDescription.length > 160 && (
                                              <span className="text-red-600 ml-2">
                                                {currentLang === 'ru' ? '(слишком длинный)' : '(too long)'}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-sm text-red-500 italic">
                                          {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Meta Keywords */}
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                      <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                      </svg>
                                      {currentLang === 'ru' ? 'Meta Keywords' : 'Meta Keywords'}
                                    </h4>
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                      {seoData.metaKeywords ? (
                                        <div className="text-sm text-gray-800 break-words">
                                          {seoData.metaKeywords}
                                          <div className="mt-1 text-xs text-gray-500">
                                            {seoData.metaKeywords.split(',').length} ключевых слов
                                          </div>
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-500 italic">
                                          {currentLang === 'ru' ? 'Не указаны' : 'Not specified'}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Заголовки */}
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                      <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                      </svg>
                                      {currentLang === 'ru' ? 'Заголовки' : 'Headings'}
                                    </h4>
                                    <div className="space-y-3">
                                      {/* H1 */}
                                      <div>
                                        <h5 className="text-xs font-medium text-gray-600 mb-1">
                                          H1 ({seoData.headings.h1.length})
                                        </h5>
                                        <div className="bg-gray-50 p-2 rounded">
                                          {seoData.headings.h1.length > 0 ? (
                                            <div className="space-y-1">
                                              {seoData.headings.h1.map((h1, index) => (
                                                <div key={index} className="text-xs text-gray-800 break-words bg-white p-2 rounded border">
                                                  {h1}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-xs text-red-500 italic">
                                              {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* H2 */}
                                      <div>
                                        <h5 className="text-xs font-medium text-gray-600 mb-1">
                                          H2 ({seoData.headings.h2.length})
                                        </h5>
                                        <div className="bg-gray-50 p-2 rounded">
                                          {seoData.headings.h2.length > 0 ? (
                                            <div className="space-y-1">
                                              {seoData.headings.h2.map((h2, index) => (
                                                <div key={index} className="text-xs text-gray-800 break-words bg-white p-2 rounded border">
                                                  {h2}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-xs text-gray-500 italic">
                                              {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* H3 */}
                                      <div>
                                        <h5 className="text-xs font-medium text-gray-600 mb-1">
                                          H3 ({seoData.headings.h3.length})
                                        </h5>
                                        <div className="bg-gray-50 p-2 rounded">
                                          {seoData.headings.h3.length > 0 ? (
                                            <div className="space-y-1">
                                              {seoData.headings.h3.map((h3, index) => (
                                                <div key={index} className="text-xs text-gray-800 break-words bg-white p-2 rounded border">
                                                  {h3}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-xs text-gray-500 italic">
                                              {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* H4 */}
                                      <div>
                                        <h5 className="text-xs font-medium text-gray-600 mb-1">
                                          H4 ({seoData.headings.h4.length})
                                        </h5>
                                        <div className="bg-gray-50 p-2 rounded">
                                          {seoData.headings.h4.length > 0 ? (
                                            <div className="space-y-1">
                                              {seoData.headings.h4.map((h4, index) => (
                                                <div key={index} className="text-xs text-gray-800 break-words bg-white p-2 rounded border">
                                                  {h4}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-xs text-gray-500 italic">
                                              {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* H5 */}
                                      <div>
                                        <h5 className="text-xs font-medium text-gray-600 mb-1">
                                          H5 ({seoData.headings.h5.length})
                                        </h5>
                                        <div className="bg-gray-50 p-2 rounded">
                                          {seoData.headings.h5.length > 0 ? (
                                            <div className="space-y-1">
                                              {seoData.headings.h5.map((h5, index) => (
                                                <div key={index} className="text-xs text-gray-800 break-words bg-white p-2 rounded border">
                                                  {h5}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <span className="text-xs text-gray-500 italic">
                                              {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                  <p className="text-sm">
                                    {currentLang === 'ru' ? 'Ошибка анализа SEO элементов' : 'Error analyzing SEO elements'}
                                  </p>
                                </div>
                              );
                            })()
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              <p className="text-sm">
                                {currentLang === 'ru' ? 'HTML код недоступен для анализа' : 'HTML code not available for analysis'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Визуальный просмотр страницы в iframe */}
                    {viewMode === 'iframe' && (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            {currentLang === 'ru' ? 'Визуальный просмотр страницы' : 'Visual Page Preview'}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              {currentLang === 'ru' ? 'Безопасный режим' : 'Safe mode'}
                            </span>
                            <div className="flex items-center space-x-1">
                              <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                              <span className="text-xs text-green-600">
                                {currentLang === 'ru' ? 'Защищено' : 'Protected'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="relative">
                          {iframeUrl ? (
                            <>
                              {iframeLoading && (
                                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                                  <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                    <p className="text-sm text-gray-600">
                                      {currentLang === 'ru' ? 'Загрузка страницы...' : 'Loading page...'}
                                    </p>
                                  </div>
                                </div>
                              )}
                              <iframe
                                src={iframeUrl}
                                className="w-full h-96 border-0"
                                title={currentLang === 'ru' ? 'Историческая версия страницы' : 'Historical page version'}
                                sandbox="allow-same-origin allow-scripts"
                                loading="lazy"
                                onLoad={handleIframeLoad}
                                onError={handleIframeError}
                              />
                            </>
                          ) : (
                            <div className="h-96 flex items-center justify-center text-gray-500">
                              <div className="text-center">
                                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-sm">
                                  {currentLang === 'ru' ? 'HTML код недоступен для просмотра' : 'HTML code not available for preview'}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* HTML код */}
                    {viewMode === 'html' && (
                      <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-300">
                            {currentLang === 'ru' ? 'HTML код страницы' : 'Page HTML Code'}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-400">
                              {selectedHistoryItem.html_code?.length || 0} символов
                            </span>
                            <button
                              onClick={() => copyToClipboard(selectedHistoryItem.html_code)}
                              className="text-gray-400 hover:text-gray-300 p-1"
                              title={currentLang === 'ru' ? 'Копировать весь код' : 'Copy all code'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="p-4 max-h-96 overflow-y-auto">
                          <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap break-words">
                            {selectedHistoryItem.html_code || (currentLang === 'ru' ? 'HTML код недоступен' : 'HTML code not available')}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                                     <div className="text-center py-8">
                     <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                     <h3 className="mt-2 text-sm font-medium text-gray-900">
                       {currentLang === 'ru' ? 'Выберите дату' : 'Select a date'}
                     </h3>
                     <p className="mt-1 text-sm text-gray-500">
                       {currentLang === 'ru' ? 'Выберите дату из списка слева для анализа SEO элементов' : 'Select a date from the list on the left to analyze SEO elements'}
                     </p>
                   </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageHistoryModal; 